import { CustomTooltip } from '@tcweb/connect-ui';
import {
  IconContextMenu,
  IconContextMenuSelectEvent,
} from '@trimble/web3d-web-components';
import { t } from '@trimble/i18n';
import { Component, Show } from 'solid-js';
import { LayerListPanelAnalyticsEvent } from '../analytics/layer-list-panel-analytics-events';
import { layerListPanelTracker } from '../analytics/layer-list-panel-tracker';
import type { LayerItemObject } from './LayerListPanel.types';

interface LayerItemProps {
  readonly item: LayerItemObject;
  readonly isSelected: boolean;
  readonly isActive?: boolean;
  readonly isLoading: boolean;
  readonly menuItems: readonly {
    readonly label: string;
    readonly value: string;
  }[];
  onSelection(event: PointerEvent, item: LayerItemObject): void;
  onToggleVisibility(item: LayerItemObject): void;
  onContextMenuSelection(event: IconContextMenuSelectEvent): void;
  onMenuTriggerRef?(guid: string, element: HTMLElement | undefined): void;
}

export const LayerItem: Component<LayerItemProps> = (props) => {
  const isVisibilityDisabled = (): boolean =>
    props.isLoading || (props.item.visibilityToggleDisabled ?? false);

  /** Tooltip shows name and suffix as one uniformly sized string. */
  const fullName = (): string =>
    `${props.item.name}${props.item.nameSuffix ?? ''}`;

  const handleSelection = (event: MouseEvent): void => {
    props.onSelection(event as PointerEvent, props.item);
  };

  const handleToggleVisibility = (event: Event): void => {
    event.stopPropagation();
    if (isVisibilityDisabled()) {
      return;
    }

    layerListPanelTracker.track(LayerListPanelAnalyticsEvent.LayerToggle);
    props.onToggleVisibility(props.item);
  };

  const stopPropagation = (event: MouseEvent): void => {
    event.stopPropagation();
  };

  const VisibilityButton: Component = () => (
    <modus-wc-button
      color="tertiary"
      custom-class={`cursor-pointer hover:opacity-70 ${!props.item.visible ? 'opacity-50' : ''}`}
      shape="circle"
      size="md"
      type="button"
      variant="borderless"
      onClick={handleToggleVisibility}
      disabled={isVisibilityDisabled()}
    >
      <modus-wc-icon
        name={props.item.visible ? 'visibility_on' : 'visibility_off'}
      />
    </modus-wc-button>
  );

  return (
    <div
      class={`h-[46px] flex flex-row items-center gap-2 select-none px-4 ${props.isSelected ? 'bg-blue-pale' : ''} hover:bg-gray-0`}
      data-testid="list-item"
      data-layer-guid={props.item.guid}
      onClick={handleSelection}
    >
      {/* eslint-disable-next-line custom/no-modus-v1 -- TWENG-132 */}
      <Show when={!props.isLoading} fallback={<modus-spinner size="24px" />}>
        <Show
          when={props.item.visibilityToggleDisabledReason}
          fallback={<VisibilityButton />}
        >
          {(reason) => (
            <CustomTooltip text={reason()} position="top-start">
              <span>
                <VisibilityButton />
              </span>
            </CustomTooltip>
          )}
        </Show>
      </Show>
      <Show when={props.item.showColorIndicator ?? true}>
        <span
          class="w-4 h-4 rounded-full flex-shrink-0"
          data-testid="layer-color-indicator"
          style={{
            ['background-color']: props.item.color,
          }}
        />
      </Show>
      <CustomTooltip
        class="flex-grow truncate w-full block"
        text={fullName()}
        position="top-start"
      >
        <span class="flex-grow text-sm truncate block">
          {props.item.name}
          <Show when={props.item.nameSuffix}>
            {(suffix) => (
              <span class="text-xs text-gray-6" textContent={suffix()} />
            )}
          </Show>
        </span>
      </CustomTooltip>
      <Show when={props.isActive}>
        <span
          class="text-xs text-gray-6 flex-shrink-0"
          data-testid="layer-active-label"
          textContent={t('shared.data-prep.active-layer')}
        />
      </Show>
      <Show
        when={(props.item.showContextMenu ?? true) && props.menuItems.length}
      >
        <div
          class="pl-4"
          onClick={stopPropagation}
          ref={(el) => {
            props.onMenuTriggerRef?.(props.item.guid, el);
          }}
        >
          <IconContextMenu
            value={props.item.guid}
            iconName="more_vertical"
            tabAligned={true}
            menuItems={[...props.menuItems]}
            onSelection={props.onContextMenuSelection}
          />
        </div>
      </Show>
    </div>
  );
};
